import { Link, useOutletContext } from "react-router-dom";
import { TriangleAlert } from "lucide-react";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { useFetcher } from "~/hooks/useFetcher";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { Agent, AgentToolkit, agentToolkitsPath, Runtime, runtimesPath } from "~/services/agents";

// Mirrors the backend `ToolkitId.CODE_INTERPRETER`. Runtimes only take effect
// when this toolkit is mounted on the agent.
const CODE_INTERPRETER_TOOLKIT_ID = "code_interpreter";

interface AgentRuntimesTabProps {
  // Overrides the outlet-context agent — used when this tab is rendered
  // outside the AgentDetail route tree (the project context sidebar).
  agent?: Agent;
}

/**
 * Read-only view of the sandbox runtimes available to the Code Interpreter
 * toolkit. Runtimes are provisioned per customer via Terraform — this tab
 * reports availability, it does not configure it. Per-agent runtime
 * selection is not yet wired.
 */
export const AgentRuntimesTab = ({ agent: agentProp }: AgentRuntimesTabProps) => {
  const outletAgent = useOutletContext<Agent>();
  const agent = agentProp ?? outletAgent;

  const { data: runtimes, isLoading } = useFetcher<Runtime[]>(runtimesPath());
  const { data: agentToolkits, isLoading: toolkitsLoading } = useFetcher<AgentToolkit[]>(
    agentToolkitsPath(agent.id)
  );

  const totalCount = runtimes?.length ?? 0;
  const codeInterpreterEnabled = (agentToolkits ?? []).some(
    (t) => t.toolkit.id === CODE_INTERPRETER_TOOLKIT_ID
  );

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <TabHeader
        title="Runtimes"
        showReadOnlyBadge
        count={isLoading || totalCount === 0 ? undefined : totalCount}
        max={isLoading || totalCount === 0 ? undefined : totalCount}
        context={isLoading ? undefined : "Sandboxes the Code Interpreter toolkit can run code in"}
        description="Runtimes are the sandboxed environments available to this agent. The Code Interpreter toolkit must still be enabled under Toolkits for the agent to use them."
      />

      {!toolkitsLoading && !codeInterpreterEnabled && (
        <Alert variant="warning">
          <TriangleAlert />
          <AlertDescription>
            <p>
              The Code Interpreter toolkit isn&apos;t enabled for this agent, so these runtimes
              aren&apos;t in use yet. Enable it under{" "}
              <Link to="../toolkits" className="font-medium underline">
                Toolkits
              </Link>
              .
            </p>
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : !runtimes?.length ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No runtimes available</p>
      ) : (
        <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {runtimes.map((runtime) => (
            <div key={runtime.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{runtime.name}</p>
                {runtime.description && (
                  <p className="text-xs text-muted-foreground">{runtime.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
